import { client } from '~/api/client';
import {
  CreateHtmlDocumentRenderGrantDocument,
  type CreateHtmlDocumentRenderGrantMutation,
  type CreateHtmlDocumentRenderGrantMutationVariables
} from '~/api/graphql';

type GrantedDocument = {
  workspaceId: string;
  artifactId: string;
  version: number;
  styleId?: string;
};

const createRenderGrant = async (document: GrantedDocument): Promise<string | null> => {
  const result = await client.mutate<
    CreateHtmlDocumentRenderGrantMutation,
    CreateHtmlDocumentRenderGrantMutationVariables
  >({
    mutation: CreateHtmlDocumentRenderGrantDocument,
    variables: document,
    fetchPolicy: 'no-cache'
  });
  if (result.errors?.length) {
    throw new Error(result.errors.map((error) => error.message).join('; '));
  }
  return result.data?.createHtmlDocumentRenderGrant.token ?? null;
};

/**
 * Fetches one of statics' measurements of a document version, which statics gives only to the
 * holder of a grant the API issued for that version. Asking for a grant on every fetch is
 * deliberate: a grant admits one request, so a cached or shared one would be refused. Without
 * a grant statics refuses the fetch, and the caller draws the unmeasured layout.
 */
export async function fetchWithRenderGrant(
  url: string,
  document: GrantedDocument,
  signal: AbortSignal
): Promise<Response> {
  const token = await createRenderGrant(document).catch((error: unknown) => {
    // eslint-disable-next-line no-console
    console.warn('[render-grant] grant refused; fetching without one', { ...document, error });
    return null;
  });
  const target = new URL(url, window.location.href);
  if (token) target.searchParams.set('grant', token);
  const response = await fetch(target.toString(), { signal, credentials: 'include' });
  if (response.status === 401 || response.status === 403) {
    // eslint-disable-next-line no-console
    console.warn('[render-grant] statics refused measurements', {
      ...document,
      route: target.pathname,
      status: response.status
    });
  }
  return response;
}
